import { cn } from "@/utils/tailwind-helper";
import type { Signal } from "@builder.io/qwik";
import { $, component$ } from "@builder.io/qwik";
import type { IGenExternalLink, IGenPage, IGenSubnavigationItem } from "../../services/graphql/__generated/sdk";
import { useNavigation } from "../../hooks/useNavigation";
import { IconChevronDown } from "../../icons/IconChevronDown";

export const SubnavigationItem = component$<{
    item: IGenSubnavigationItem;
    mainTextColor?: string | null | undefined | string[];
    index?: number;
}>(({ item, mainTextColor, index }) => {
    const { getLink } = useNavigation();

    const prepItem = item.externalLink
        ? ({ __typename: "ExternalLink", linkAddress: item.externalLink, name: item.label } as IGenExternalLink)
        : item?.linkToCurrentPageComponent
          ? null
          : item?.linkToPage;

    const link = getLink(prepItem);
    if (item?.linkToCurrentPageComponent?.id) {
        return (
            <div>
                <a
                    {...link}
                    class={cn(
                        index !== 0 ? "mt-4" : "mt-0",
                        " flex h-[28px] items-center",
                        "silver:mt-0 gold:h-[32px]",
                        "platinum:h-[32px]",
                        "transition-colors duration-300",
                        "cursor-pointer",
                        mainTextColor,
                    )}
                    onClick$={(e) => {
                        if (typeof window === "undefined") return;
                        e.preventDefault();
                        e.stopPropagation();

                        const element = item?.linkToCurrentPageComponent?.id && document.getElementById(item?.linkToCurrentPageComponent?.id);
                        if (!element) return;

                        const headerOffset = 120; // 120px offset
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth"
                        });
                    }}
                >
                    {item?.label}
                </a>
            </div>
        );
    }

    return (
        <div>
            <a
                {...link}
                class={cn(
                    index !== 0 ? "mt-4" : "mt-0",
                    " flex h-[28px] items-center",
                    "silver:mt-0 gold:h-[32px]",
                    "platinum:h-[32px]",
                    "transition-colors duration-300",
                    "cursor-pointer",
                    mainTextColor,
                )}
            >
                {item?.label}
            </a>
        </div>
    );
});

export const Subnavigation = component$<{
    subnavigationTitle?: string | null | undefined;
    subnavigationItems?: IGenPage["subnavigationItems"] | null | undefined;
    mainTextColor?: string | null | undefined | string[];
    navigationBackground?: string | null | undefined | string[];
    mobileSubmenuOpen: Signal<boolean>;
}>(({ subnavigationTitle, subnavigationItems, mainTextColor, mobileSubmenuOpen }) => {
    return (
        <>
            <div
                class={cn(
                    "grid grid-cols-4 items-center justify-between gap-x-[16px] px-[16px] py-[24px] silver:grid-cols-8 silver:gap-x-[24px] silver:px-[32px] gold:grid-cols-12 gold:gap-x-[24px] gold:px-[40px] platinum:mx-auto platinum:max-w-[1440px] platinum:grid-cols-12 platinum:gap-x-[24px] platinum:px-[40px]",
                )}
            >
                <div
                    class={cn(
                        "col-span-4",
                        "silver:col-span-3",
                        "gold:col-span-4",
                        "platinum:col-span-4",
                        "transition-colors duration-300",
                        mainTextColor,
                    )}
                >
                    {subnavigationTitle && <h1 class="text-base font-medium leading-6">{subnavigationTitle}</h1>}
                </div>
                <div class={cn("absolute right-0 bottom-0", "silver:hidden")}>
                    <div
                        class="px-4 py-5"
                        onClick$={$(() => {
                            mobileSubmenuOpen.value = !mobileSubmenuOpen.value;
                        })}
                    >
                        <span
                            class={cn(
                                "flex cursor-pointer flex-row-reverse items-center",
                                mainTextColor,
                                mobileSubmenuOpen.value ? "rotate-180" : "",
                            )}
                        >
                            <IconChevronDown />
                        </span>
                    </div>
                </div>
                {/* this below is only from silver upwards used */}
                <div class={cn("hidden silver:grid", "silver:col-span-5", "gold:col-span-8", "platinum:col-span-8")}>
                    <div
                        class={cn(
                            "silder:justify-self-end silver:pointer-events-all flex-col justify-end silver:flex",
                            "silver:flex-row silver:gap-x-12",
                        )}
                    >
                        {subnavigationItems?.map((item, index) => {
                            if (!item) return null;
                            return (
                                <SubnavigationItem
                                    item={item}
                                    mainTextColor={mainTextColor}
                                    key={`sn${item?.id}${index}`}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
});
